import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ============ DEFAULT THEME (fallback) ============
const DEFAULT_THEME = {
  primary: '#d4a053',
  primaryDark: '#b8860b',
  primaryLight: '#f0d68a',
  secondary: '#f472b6',
  dark: '#3d2c1e',
  text: '#334155',
  muted: '#64748b',
  light: '#fefcf8',
  tableHeader: '#d4a053',
  tableAlt: '#fef9f0',
  border: '#f5e6d3',
  white: '#ffffff',
};

// Build a PDF theme from school's ThemeContext colors
function buildPdfTheme(schoolColors) {
  if (!schoolColors) return DEFAULT_THEME;
  return {
    primary: schoolColors.primary || DEFAULT_THEME.primary,
    primaryDark: schoolColors.primaryDark || DEFAULT_THEME.primaryDark,
    primaryLight: schoolColors.primaryLight || DEFAULT_THEME.primaryLight,
    secondary: schoolColors.secondary || DEFAULT_THEME.secondary,
    dark: schoolColors.textPrimary || DEFAULT_THEME.dark,
    text: schoolColors.textSecondary || DEFAULT_THEME.text,
    muted: schoolColors.textMuted || DEFAULT_THEME.muted,
    light: schoolColors.bodyBg || DEFAULT_THEME.light,
    tableHeader: schoolColors.primary || DEFAULT_THEME.tableHeader,
    tableAlt: lightenColor(schoolColors.primary || DEFAULT_THEME.primary, 0.92),
    border: schoolColors.border || DEFAULT_THEME.border,
    white: '#ffffff',
  };
}

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
}

// Create a very light tint of a color (for table alternating rows)
function lightenColor(hex, factor) {
  const [r, g, b] = hexToRgb(hex);
  const lr = Math.round(r + (255 - r) * factor);
  const lg = Math.round(g + (255 - g) * factor);
  const lb = Math.round(b + (255 - b) * factor);
  return `#${lr.toString(16).padStart(2, '0')}${lg.toString(16).padStart(2, '0')}${lb.toString(16).padStart(2, '0')}`;
}

// ============ SHARED HELPERS ============

function drawHeader(doc, schoolName, subtitle, title, THEME, logo) {
  const pageWidth = doc.internal.pageSize.getWidth();

  // Header bar with school's primary dark color
  doc.setFillColor(...hexToRgb(THEME.primaryDark));
  doc.rect(0, 0, pageWidth, 28, 'F');
  doc.setFillColor(...hexToRgb(THEME.primary));
  doc.rect(0, 28, pageWidth, 4, 'F');

  // Logo on the left if available
  let textStartX = pageWidth / 2;
  if (logo) {
    try {
      doc.addImage(logo, 'PNG', 10, 3, 22, 22);
    } catch (e) {
      // logo failed to load — skip it
    }
  }

  // School name
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(schoolName, pageWidth / 2, 14, { align: 'center' });
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(subtitle, pageWidth / 2, 24, { align: 'center' });

  // Title bar
  doc.setFillColor(...hexToRgb(THEME.primary));
  doc.rect(0, 32, pageWidth, 20, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(title, pageWidth / 2, 44, { align: 'center' });

  doc.setTextColor(...hexToRgb(THEME.dark));
  return 62;
}

function drawFooter(doc, text, THEME) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const footerBg = lightenColor(THEME.primary, 0.93);
  doc.setFillColor(...hexToRgb(footerBg));
  doc.rect(0, pageHeight - 25, pageWidth, 25, 'F');
  doc.setFontSize(7);
  doc.setTextColor(...hexToRgb(THEME.muted));
  doc.setFont('helvetica', 'normal');
  doc.text(text || 'This is a computer-generated document.', pageWidth / 2, pageHeight - 15, { align: 'center' });
  doc.text(`Generated on ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}`, pageWidth / 2, pageHeight - 7, { align: 'center' });
}

function drawSignatures(doc, labels, y, THEME) {
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...hexToRgb(THEME.text));
  const positions = [55, 165, 275];
  labels.forEach((label, i) => {
    if (positions[i] !== undefined) {
      doc.text('______________________', positions[i], y);
      doc.text(label, positions[i], y + 12);
    }
  });
}

// ============ 1. REPORT CARD ============

export function generateReportCard(student, school, marks = [], themeColors, logo) {
  const THEME = buildPdfTheme(themeColors);
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();

  let y = drawHeader(doc, school.name, school.address || 'Knowledge City, Education Lane', 'REPORT CARD / PROGRESS REPORT', THEME, logo);

  // Student info box
  y += 5;
  doc.setFillColor(...hexToRgb(THEME.tableAlt));
  doc.setDrawColor(...hexToRgb(THEME.border));
  doc.roundedRect(15, y, pageWidth - 30, 28, 2, 2, 'FD');

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...hexToRgb(THEME.dark));
  doc.text(`Student Name: ${student.firstName} ${student.lastName}`, 20, y + 8);
  doc.text(`Class: ${student.class} - ${student.section}`, 120, y + 8);
  doc.setFont('helvetica', 'normal');
  doc.text(`Admission No: ${student.admissionNo}`, 20, y + 16);
  doc.text(`Roll No: ${student.rollNo || 'N/A'}`, 120, y + 16);
  doc.text(`Father's Name: ${student.fatherName || 'N/A'}`, 20, y + 24);
  doc.text(`Academic Year: ${student.academicYear || '2025-2026'}`, 120, y + 24);

  y += 36;

  // Group marks by exam
  const exams = {};
  marks.forEach(m => {
    if (!exams[m.examName]) exams[m.examName] = [];
    exams[m.examName].push(m);
  });

  // If no marks, show sample
  if (Object.keys(exams).length === 0) {
    exams['Unit Test 1'] = [
      { subject: 'Mathematics', maxMarks: 100, obtainedMarks: 85, percentage: 85, grade: 'A' },
      { subject: 'Science', maxMarks: 100, obtainedMarks: 78, percentage: 78, grade: 'B+' },
      { subject: 'English', maxMarks: 100, obtainedMarks: 92, percentage: 92, grade: 'A+' },
      { subject: 'Hindi', maxMarks: 100, obtainedMarks: 70, percentage: 70, grade: 'B+' },
      { subject: 'Social Science', maxMarks: 100, obtainedMarks: 82, percentage: 82, grade: 'A' },
    ];
    exams['Half Yearly'] = [
      { subject: 'Mathematics', maxMarks: 100, obtainedMarks: 88, percentage: 88, grade: 'A' },
      { subject: 'Science', maxMarks: 100, obtainedMarks: 82, percentage: 82, grade: 'A' },
      { subject: 'English', maxMarks: 100, obtainedMarks: 95, percentage: 95, grade: 'A+' },
      { subject: 'Hindi', maxMarks: 100, obtainedMarks: 75, percentage: 75, grade: 'B+' },
      { subject: 'Social Science', maxMarks: 100, obtainedMarks: 86, percentage: 86, grade: 'A' },
    ];
  }

  for (const [examName, examMarks] of Object.entries(exams)) {
    if (y > 240) { doc.addPage(); y = 20; }

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...hexToRgb(THEME.primaryDark));
    doc.text(examName, 15, y);
    y += 3;

    const totalMax = examMarks.reduce((s, m) => s + Number(m.maxMarks), 0);
    const totalObt = examMarks.reduce((s, m) => s + Number(m.obtainedMarks), 0);
    const overallPct = totalMax > 0 ? ((totalObt / totalMax) * 100).toFixed(1) : '0';

    const rows = examMarks.map(m => [m.subject, m.maxMarks, m.obtainedMarks, `${Number(m.percentage).toFixed(1)}%`, m.grade]);
    rows.push([{ content: 'TOTAL', styles: { fontStyle: 'bold' } }, totalMax, totalObt, `${overallPct}%`, '']);

    autoTable(doc, {
      startY: y,
      head: [['Subject', 'Max Marks', 'Obtained', 'Percentage', 'Grade']],
      body: rows,
      margin: { left: 15, right: 15 },
      styles: { fontSize: 9, cellPadding: 3, textColor: hexToRgb(THEME.dark) },
      headStyles: { fillColor: hexToRgb(THEME.primary), textColor: [255, 255, 255], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: hexToRgb(THEME.tableAlt) },
      columnStyles: { 0: { cellWidth: 55 }, 1: { halign: 'center' }, 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' } },
    });

    y = doc.lastAutoTable.finalY + 10;
  }

  // Grading scale
  if (y > 250) { doc.addPage(); y = 20; }
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...hexToRgb(THEME.muted));
  doc.text('Grading Scale:', 15, y);
  doc.setFont('helvetica', 'normal');
  doc.text('A+ (90-100) | A (80-89) | B+ (70-79) | B (60-69) | C (50-59) | D (40-49) | F (Below 40)', 15, y + 6);

  // Signatures
  const sigY = doc.internal.pageSize.getHeight() - 45;
  drawSignatures(doc, ["Class Teacher's Signature", "Principal's Signature", "Parent's Signature"], sigY, THEME);
  drawFooter(doc, null, THEME);

  return doc;
}

// ============ 2. ID CARD ============

export function generateIDCard(student, school, themeColors, logo) {
  const THEME = buildPdfTheme(themeColors);
  const doc = new jsPDF('l', 'mm', [90, 55]);

  // FRONT
  doc.setFillColor(...hexToRgb(THEME.primaryDark));
  doc.rect(0, 0, 90, 16, 'F');
  doc.setFillColor(...hexToRgb(THEME.primary));
  doc.rect(0, 16, 90, 2, 'F');

  // Logo on front
  if (logo) {
    try { doc.addImage(logo, 'PNG', 3, 2, 12, 12); } catch (e) { /* skip */ }
  }

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text(school.name || 'Springfield Academy', 45, 6, { align: 'center' });
  doc.setFontSize(5);
  doc.setFont('helvetica', 'normal');
  doc.text(school.address || 'Knowledge City, Education Lane', 45, 11, { align: 'center' });

  // ID Card title
  doc.setFillColor(...hexToRgb(THEME.primary));
  doc.rect(0, 18, 90, 6, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(6);
  doc.setFont('helvetica', 'bold');
  doc.text('STUDENT IDENTITY CARD', 45, 22, { align: 'center' });

  // Photo placeholder
  doc.setDrawColor(...hexToRgb(THEME.primary));
  doc.setLineWidth(0.5);
  doc.rect(5, 27, 18, 22);
  doc.setFontSize(5);
  doc.setTextColor(...hexToRgb(THEME.muted));
  doc.text('PHOTO', 14, 39, { align: 'center' });

  // Student details
  doc.setTextColor(...hexToRgb(THEME.dark));
  const dx = 27;
  let dy = 28;

  const addField = (label, value) => {
    doc.setFontSize(4);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...hexToRgb(THEME.muted));
    doc.text(label, dx, dy);
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...hexToRgb(THEME.dark));
    doc.text(value || 'N/A', dx, dy + 3);
    dy += 7;
  };

  addField('NAME', `${student.firstName} ${student.lastName}`);
  addField('CLASS / SECTION', `${student.class} - ${student.section}`);
  addField('ADMISSION NO', student.admissionNo);

  // Bottom bar
  const bottomBg = lightenColor(THEME.primary, 0.93);
  doc.setFillColor(...hexToRgb(bottomBg));
  doc.rect(0, 50, 90, 5, 'F');
  doc.setFontSize(4);
  doc.setTextColor(...hexToRgb(THEME.muted));
  doc.text(`Valid: ${new Date().getFullYear()}-${new Date().getFullYear() + 1} | Blood: ${student.bloodGroup || 'N/A'}`, 45, 53, { align: 'center' });

  // BACK page
  doc.addPage([90, 55]);
  doc.setFillColor(...hexToRgb(THEME.primaryDark));
  doc.rect(0, 0, 90, 10, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(6);
  doc.setFont('helvetica', 'bold');
  doc.text('IMPORTANT INFORMATION', 45, 7, { align: 'center' });

  dy = 14;
  doc.setFontSize(5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...hexToRgb(THEME.text));
  const rules = [
    '1. This card must be carried at all times.',
    '2. If found, please return to school office.',
    '3. Loss of card must be reported immediately.',
    `4. Father: ${student.fatherName || 'N/A'}`,
    `5. Contact: ${student.contactPhone || 'N/A'}`,
    `6. Emergency: ${student.emergencyContact || 'N/A'}`,
  ];
  rules.forEach(r => {
    doc.text(r, 5, dy);
    dy += 5;
  });

  doc.setFontSize(4);
  doc.text('______________________', 55, 45);
  doc.text("Principal's Signature", 55, 49);

  return doc;
}

// ============ 3. TRANSFER CERTIFICATE ============

export function generateTC(student, school, reason = 'Parent Request', themeColors, logo) {
  const THEME = buildPdfTheme(themeColors);
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();

  let y = drawHeader(doc, school.name, school.address || 'Knowledge City, Education Lane', 'TRANSFER CERTIFICATE', THEME, logo);

  // TC Number
  y += 5;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...hexToRgb(THEME.muted));
  doc.text(`TC No: TC/${new Date().getFullYear()}/${String(student.id || 1).toString().padStart(4, '0')}`, pageWidth - 20, y, { align: 'right' });
  y += 10;

  const fields = [
    ['Name of the Student', `${student.firstName} ${student.lastName}`],
    ["Father's Name", student.fatherName || 'N/A'],
    ["Mother's Name", student.motherName || 'N/A'],
    ['Date of Birth', student.dateOfBirth || 'N/A'],
    ['Admission Number', student.admissionNo],
    ['Date of Admission', student.admissionDate || 'N/A'],
    ['Class at time of leaving', `${student.class} - ${student.section}`],
    ['Medium of Instruction', 'English'],
    ['Qualified for promotion', 'Yes'],
    ['All dues paid', 'Yes'],
    ['Character & Conduct', 'Good'],
    ['Date of leaving', new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })],
    ['Reason for leaving', reason],
    ['General Remarks', 'N/A'],
  ];

  autoTable(doc, {
    startY: y,
    body: fields.map((f, i) => [{ content: `${i + 1}. ${f[0]}`, styles: { fontStyle: 'bold', textColor: hexToRgb(THEME.muted) } }, f[1]]),
    margin: { left: 15, right: 15 },
    styles: { fontSize: 9, cellPadding: 4, textColor: hexToRgb(THEME.dark) },
    alternateRowStyles: { fillColor: hexToRgb(THEME.tableAlt) },
    columnStyles: { 0: { cellWidth: 70 } },
    theme: 'plain',
    didDrawCell: (data) => {
      doc.setDrawColor(...hexToRgb(THEME.border));
      doc.setLineWidth(0.2);
      doc.line(data.cell.x, data.cell.y + data.cell.height, data.cell.x + data.cell.width, data.cell.y + data.cell.height);
    }
  });

  y = doc.lastAutoTable.finalY + 20;
  drawSignatures(doc, ['Class Teacher', 'Principal', 'School Seal'], y, THEME);
  drawFooter(doc, 'This Transfer Certificate is issued on request. No separate recommendation is implied.', THEME);

  return doc;
}

// ============ 4. BONAFIDE CERTIFICATE ============

export function generateBonafide(student, school, purpose = '', academicYear = '', themeColors, logo) {
  const THEME = buildPdfTheme(themeColors);
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();

  let y = drawHeader(doc, school.name, school.address || 'Knowledge City, Education Lane', 'BONAFIDE CERTIFICATE', THEME, logo);

  y += 8;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...hexToRgb(THEME.muted));
  doc.text(`Ref No: BF/${new Date().getFullYear()}/${String(student.id || 1).toString().padStart(4, '0')}`, pageWidth - 20, y, { align: 'right' });
  y += 6;
  doc.text(`Date: ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}`, pageWidth - 20, y, { align: 'right' });

  y += 18;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...hexToRgb(THEME.primaryDark));
  doc.text('TO WHOM IT MAY CONCERN', pageWidth / 2, y, { align: 'center' });

  y += 5;
  doc.setDrawColor(...hexToRgb(THEME.primary));
  doc.setLineWidth(0.8);
  doc.line(pageWidth / 2 - 30, y, pageWidth / 2 + 30, y);

  y += 15;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...hexToRgb(THEME.text));

  const year = academicYear || `${new Date().getFullYear()}-${new Date().getFullYear() + 1}`;
  const body1 = `This is to certify that ${student.firstName} ${student.lastName}, son/daughter of ${student.fatherName || '___________'}, bearing Admission Number ${student.admissionNo}, is a bonafide student of this institution, studying in Class ${student.class} Section ${student.section} during the academic year ${year}.`;

  doc.text(body1, 20, y, { maxWidth: pageWidth - 40, lineHeightFactor: 1.8, align: 'justify' });

  y += 40;
  const dob = student.dateOfBirth || '___________';
  doc.text(`His/Her date of birth as per our school records is ${dob}.`, 20, y, { maxWidth: pageWidth - 40, lineHeightFactor: 1.8 });

  y += 18;
  const purposeText = purpose || '___________________________';
  doc.text(`This certificate is issued at the request of the student/parent for the purpose of ${purposeText}.`, 20, y, { maxWidth: pageWidth - 40, lineHeightFactor: 1.8 });

  y += 25;
  doc.text('We wish him/her all the best in future endeavors.', 20, y);

  const sigY = doc.internal.pageSize.getHeight() - 55;
  drawSignatures(doc, ['Principal', '', 'School Seal'], sigY, THEME);
  drawFooter(doc, 'This bonafide certificate is issued on request and does not serve as a transfer certificate.', THEME);

  return doc;
}

// ============ 5. CHARACTER CERTIFICATE ============

export function generateCharacterCert(student, school, themeColors, logo) {
  const THEME = buildPdfTheme(themeColors);
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();

  let y = drawHeader(doc, school.name, school.address || 'Knowledge City, Education Lane', 'CHARACTER CERTIFICATE', THEME, logo);

  y += 8;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...hexToRgb(THEME.muted));
  doc.text(`Ref No: CC/${new Date().getFullYear()}/${String(student.id || 1).toString().padStart(4, '0')}`, pageWidth - 20, y, { align: 'right' });
  y += 6;
  doc.text(`Date: ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}`, pageWidth - 20, y, { align: 'right' });

  y += 18;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...hexToRgb(THEME.primaryDark));
  doc.text('TO WHOM IT MAY CONCERN', pageWidth / 2, y, { align: 'center' });

  y += 5;
  doc.setDrawColor(...hexToRgb(THEME.primary));
  doc.setLineWidth(0.8);
  doc.line(pageWidth / 2 - 30, y, pageWidth / 2 + 30, y);

  y += 15;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...hexToRgb(THEME.text));

  const schoolName = school.name || 'this institution';
  const admDate = student.admissionDate || '___________';
  const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });

  const para1 = `This is to certify that ${student.firstName} ${student.lastName}, son/daughter of ${student.fatherName || '___________'}, bearing Admission Number ${student.admissionNo}, has been a student of ${schoolName} from ${admDate} to ${today}.`;
  doc.text(para1, 20, y, { maxWidth: pageWidth - 40, lineHeightFactor: 1.8, align: 'justify' });

  y += 40;
  const para2 = `During his/her stay in this institution, ${student.firstName} has maintained good conduct and shown exemplary behavior. He/She has been regular in attendance, sincere in studies, and has actively participated in co-curricular activities.`;
  doc.text(para2, 20, y, { maxWidth: pageWidth - 40, lineHeightFactor: 1.8, align: 'justify' });

  y += 35;
  const para3 = 'To the best of our knowledge, he/she bears a good moral character and has not been involved in any act of indiscipline during his/her association with this institution.';
  doc.text(para3, 20, y, { maxWidth: pageWidth - 40, lineHeightFactor: 1.8, align: 'justify' });

  y += 30;
  doc.text('We wish him/her all the best in his/her future endeavors.', 20, y);

  const sigY = doc.internal.pageSize.getHeight() - 55;
  drawSignatures(doc, ['Class Teacher', 'Principal', 'School Seal'], sigY, THEME);
  drawFooter(doc, 'This certificate is issued upon request and does not imply any specific recommendation.', THEME);

  return doc;
}
